import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import i18next from 'i18next';
import { handleError } from '../hooks/managers/errorHandler';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** 'page'：整页降级（全局/路由级）；'card'：局部卡片降级（阅读页等分享场景） */
  variant?: 'page' | 'card';
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * 全局错误边界：任何一个组件渲染抛错不再导致整站白屏（05-5.13）。
 * 用法：全局（Application）、路由级（MLayout Outlet 外）、阅读页（/view）。
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    handleError(error, {
      context: `ErrorBoundary${info.componentStack ? ': ' + info.componentStack.split('\n')[1]?.trim() : ''}`,
      showToast: false,
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { variant = 'page' } = this.props;
    const title = i18next.t('errorBoundaryTitle', '页面出了点问题');
    const description = i18next.t('errorBoundaryDescription', '渲染时发生错误，可以尝试重新加载。');
    const retry = i18next.t('errorBoundaryRetry', '重新加载');

    const content = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          height: variant === 'page' ? '100%' : 'auto',
          minHeight: variant === 'page' ? '50vh' : '160px',
          padding: 3,
          textAlign: 'center',
        }}
      >
        <ErrorOutline color="error" />
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
        <Button variant="contained" size="small" onClick={this.handleReset}>
          {retry}
        </Button>
      </Box>
    );

    if (variant === 'card') {
      return <Paper elevation={2} sx={{ margin: 2 }}>{content}</Paper>;
    }
    return content;
  }
}

export default ErrorBoundary;
